import memoizeOne, { type MemoizedFn } from 'memoize-one';
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { branchDetailsEntryPoint as branchDetailsEntryPointOld } from '@atlassian/jira-development-board-branch-details-popup/entrypoint.tsx';
import { branchDetailsEntryPoint } from '@atlassian/jira-development-board-branch-details-popup-new/entrypoint.tsx';
import { commitDetailsEntryPoint as commitDetailsEntryPointOld } from '@atlassian/jira-development-board-commit-details-popup/entrypoint.tsx';
import { commitDetailsEntryPoint } from '@atlassian/jira-development-board-commit-details-popup-new/entrypoint.tsx';
import {
	ButtonStatus,
	DevInfoTypes,
	type ButtonStatusType,
	type DevInfoType,
	type PopupOpenTrigger,
	type PopupTriggerProps,
	type QueryOptions,
} from '@atlassian/jira-development-board-common/src/types.tsx';
import { deploymentDetailsEntryPoint as deploymentDetailsEntryPointOld } from '@atlassian/jira-development-board-deployment-details-popup/entrypoint.tsx';
import { deploymentDetailsEntryPoint } from '@atlassian/jira-development-board-deployment-details-popup-new/entrypoint.tsx';
import { designDetailsEntryPoint } from '@atlassian/jira-development-board-design-details-popup/entrypoint.tsx';
import { pullRequestDetailsEntryPoint as pullRequestDetailsEntryPointOld } from '@atlassian/jira-development-board-pr-details-popup/entrypoint.tsx';
import { pullRequestDetailsNewEntryPoint as pullRequestDetailsEntryPoint } from '@atlassian/jira-development-board-pr-details-popup-new/entrypoint.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type { Metric } from '@atlassian/browser-metrics/types';
import type { PRDetailsPopupViewNewProps } from '@atlassian/jira-development-board-pr-details-popup-new/src/ui/types.tsx';
import type { BranchDetailsPopupViewBranchDetailsPopupViewQuery } from '@atlassian/jira-relay/src/__generated__/BranchDetailsPopupViewBranchDetailsPopupViewQuery.graphql.ts';
import type { BranchDetailsPopupViewQuery } from '@atlassian/jira-relay/src/__generated__/BranchDetailsPopupViewQuery.graphql.ts';
import type { CommitDetailsPopupViewCommitDetailsPopupViewQuery } from '@atlassian/jira-relay/src/__generated__/CommitDetailsPopupViewCommitDetailsPopupViewQuery.graphql.ts';
import type { CommitDetailsPopupViewQuery } from '@atlassian/jira-relay/src/__generated__/CommitDetailsPopupViewQuery.graphql.ts';
import type { DeploymentDetailsPopupQuery } from '@atlassian/jira-relay/src/__generated__/DeploymentDetailsPopupQuery.graphql.ts';
import type { DeploymentDetailsPopupUiQuery } from '@atlassian/jira-relay/src/__generated__/DeploymentDetailsPopupUiQuery.graphql.ts';
import type { DesignDetailsPopupQuery } from '@atlassian/jira-relay/src/__generated__/DesignDetailsPopupQuery.graphql.ts';
import type { PRDetailsPopupViewQuery } from '@atlassian/jira-relay/src/__generated__/PRDetailsPopupViewQuery.graphql.ts';
import type { PRDetailsPopupViewUiQuery } from '@atlassian/jira-relay/src/__generated__/PRDetailsPopupViewUiQuery.graphql.ts';
import {
	branchDetailsPopupLoad,
	commitDetailsPopupLoad,
	deploymentDetailsPopupLoad,
	designDetailsPopupLoad,
	prDetailsPopupLoad,
} from './utils/performance-analytics/index.tsx';
import messages from './messages.tsx';

type IconColor = Record<DevInfoType, Record<ButtonStatusType, string>>;

export const HOVER_TIMEOUT_MS = 300;
export const DevInfoButtonLabelMapping: MemoizedFn<
	(formatMessage: FormatMessage) => {
		branch: string;
		commit: string;
		pullrequest: string;
		merged: string;
		declined: string;
		deployment: string;
		design: string;
	}
> = memoizeOne(
	(
		formatMessage: FormatMessage,
	): {
		branch: string;
		commit: string;
		pullrequest: string;
		merged: string;
		declined: string;
		deployment: string;
		design: string;
	} => ({
		[DevInfoTypes.BRANCH]: formatMessage(messages.branchIconButtonLabel),
		[DevInfoTypes.COMMIT]: formatMessage(messages.commitIconButtonLabel),
		[DevInfoTypes.PULLREQUEST]: formatMessage(messages.pullRequestIconButtonLabel),
		[DevInfoTypes.MERGED]: formatMessage(messages.pullRequestIconButtonLabel),
		[DevInfoTypes.DECLINED]: formatMessage(messages.pullRequestIconButtonLabel),
		[DevInfoTypes.DEPLOYMENT]: formatMessage(messages.deploymentIconButtonLabel),
		[DevInfoTypes.DESIGN]: formatMessage(messages.designIconButtonLabel),
	}),
);

export const iconColor: IconColor = {
	[DevInfoTypes.BRANCH]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.COMMIT]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.PULLREQUEST]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.MERGED]: {
		[ButtonStatus.ACTIVE]: token('color.icon.success'),
		[ButtonStatus.HOVER]: token('color.icon.success'),
		[ButtonStatus.DEFAULT]: token('color.icon.success'),
	},
	[DevInfoTypes.DECLINED]: {
		[ButtonStatus.ACTIVE]: token('color.icon.danger'),
		[ButtonStatus.HOVER]: token('color.icon.danger'),
		[ButtonStatus.DEFAULT]: token('color.icon.danger'),
	},
	[DevInfoTypes.DEPLOYMENT]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
	[DevInfoTypes.DESIGN]: {
		[ButtonStatus.ACTIVE]: token('color.icon.inverse'),
		[ButtonStatus.HOVER]: token('color.icon.subtle'),
		[ButtonStatus.DEFAULT]: token('color.icon.subtle'),
	},
};

export const experienceDetails: {
	branch: {
		id: string;
		name: string;
	};
	commit: {
		id: string;
		name: string;
	};
	deployment: {
		id: string;
		name: string;
	};
	pullrequest: {
		id: string;
		name: string;
	};
	design: {
		id: string;
		name: string;
	};
} = {
	[DevInfoTypes.BRANCH]: {
		id: 'branchDetailsPopup',
		name: 'jiraSoftwareBoardBranchDetailsPopup',
	},
	[DevInfoTypes.COMMIT]: {
		id: 'commitDetailsPopup',
		name: 'jiraSoftwareBoardCommitDetailsPopup',
	},
	[DevInfoTypes.DEPLOYMENT]: {
		id: 'deploymentDetailsPopup',
		name: 'jiraSoftwareBoardDeploymentDetailsPopup',
	},
	[DevInfoTypes.PULLREQUEST]: { id: 'prDetailsPopup', name: 'jiraSoftwareBoardPrDetailsPopup' },
	[DevInfoTypes.DESIGN]: {
		id: 'designDetailsPopup',
		name: 'jiraSoftwareBoardDesignDetailsPopup',
	},
};

export const metrics: {
	branch: Metric;
	commit: Metric;
	deployment: Metric;
	pullrequest: Metric;
	design: Metric;
} = {
	[DevInfoTypes.BRANCH]: branchDetailsPopupLoad,
	[DevInfoTypes.COMMIT]: commitDetailsPopupLoad,
	[DevInfoTypes.DEPLOYMENT]: deploymentDetailsPopupLoad,
	[DevInfoTypes.PULLREQUEST]: prDetailsPopupLoad,
	[DevInfoTypes.DESIGN]: designDetailsPopupLoad,
};

export const packageDetails: {
	branch: {
		id: string;
		packageName: string;
		teamName: string;
	};
	commit: {
		id: string;
		packageName: string;
		teamName: string;
	};
	deployment: {
		id: string;
		packageName: string;
		teamName: string;
	};
	pullrequest: {
		id: string;
		packageName: string;
		teamName: string;
	};
	design: {
		id: string;
		packageName: string;
		teamName: string;
	};
} = {
	[DevInfoTypes.BRANCH]: {
		id: 'renderBranchDetailsPopup',
		packageName: 'jiraDevelopmentBoardBranchDetailsPopup',
		teamName: 'jolt',
	},
	[DevInfoTypes.COMMIT]: {
		id: 'renderCommitDetailsPopup',
		packageName: 'jiraDevelopmentBoardCommitDetailsPopup',
		teamName: 'jolt',
	},
	[DevInfoTypes.DEPLOYMENT]: {
		id: 'renderDeploymentDetailsPopup',
		packageName: 'jiraDevelopmentBoardDeploymentDetailsPopup',
		teamName: 'jolt',
	},
	[DevInfoTypes.PULLREQUEST]: {
		id: 'renderPRDetailsPopup',
		packageName: 'jiraDevelopmentBoardPrDetailsPopup',
		teamName: 'jolt',
	},
	[DevInfoTypes.DESIGN]: {
		id: 'renderDesignDetailsPopup',
		packageName: 'jiraDevelopmentBoardDesignDetailsPopup',
		teamName: 'helios',
	},
};

export const popupEntryPointsOld: {
	branch: EntryPoint<
		EntryPointComponent<
			{
				branchDetailsForIssue: BranchDetailsPopupViewQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
	commit: EntryPoint<
		EntryPointComponent<
			{
				commitDetailsForIssue: CommitDetailsPopupViewQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
	deployment: EntryPoint<
		EntryPointComponent<
			{
				deployments: DeploymentDetailsPopupQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
	pullrequest: EntryPoint<
		EntryPointComponent<
			{
				pullRequestDetailsForIssue: PRDetailsPopupViewQuery;
			},
			{},
			{
				issueAri: string;
				devInfoType: DevInfoType;
				onReady: () => void;
				trigger?: PopupOpenTrigger;
				queryOptions?: QueryOptions;
				scopeId?: string;
			},
			{}
		>,
		{
			issueId: string;
			isNetworkOnly?: boolean;
		}
	>;
	design: EntryPoint<
		EntryPointComponent<
			{
				designDetailsPopup: DesignDetailsPopupQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
} = {
	[DevInfoTypes.BRANCH]: branchDetailsEntryPointOld,
	[DevInfoTypes.COMMIT]: commitDetailsEntryPointOld,
	[DevInfoTypes.DEPLOYMENT]: deploymentDetailsEntryPointOld,
	[DevInfoTypes.PULLREQUEST]: pullRequestDetailsEntryPointOld,
	[DevInfoTypes.DESIGN]: designDetailsEntryPoint,
};

export const popupEntryPoints: {
	branch: EntryPoint<
		EntryPointComponent<
			{
				branchDetailsForIssue: BranchDetailsPopupViewBranchDetailsPopupViewQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
	commit: EntryPoint<
		EntryPointComponent<
			{
				commitDetailsForIssue: CommitDetailsPopupViewCommitDetailsPopupViewQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
	deployment: EntryPoint<
		EntryPointComponent<
			{
				deployments: DeploymentDetailsPopupUiQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
	pullrequest: EntryPoint<
		EntryPointComponent<
			{
				pullRequestDetailsForIssue: PRDetailsPopupViewUiQuery;
			},
			{},
			PRDetailsPopupViewNewProps,
			{}
		>,
		{
			issueId: string;
			isNetworkOnly?: boolean;
		}
	>;
	design: EntryPoint<
		EntryPointComponent<
			{
				designDetailsPopup: DesignDetailsPopupQuery;
			},
			{},
			PopupTriggerProps,
			{}
		>,
		{
			issueId: string;
		}
	>;
} = {
	[DevInfoTypes.BRANCH]: branchDetailsEntryPoint,
	[DevInfoTypes.COMMIT]: commitDetailsEntryPoint,
	[DevInfoTypes.DEPLOYMENT]: deploymentDetailsEntryPoint,
	[DevInfoTypes.PULLREQUEST]: pullRequestDetailsEntryPoint,
	[DevInfoTypes.DESIGN]: designDetailsEntryPoint,
};

export const DeploymentState: Readonly<{
	SUCCESSFUL: 'SUCCESSFUL';
	FAILED: 'FAILED';
}> = Object.freeze({
	SUCCESSFUL: 'SUCCESSFUL',
	FAILED: 'FAILED',
});

export const DeploymentEnvironment: Readonly<{
	PRODUCTION: 'PRODUCTION';
	STAGING: 'STAGING';
	TESTING: 'TESTING';
}> = Object.freeze({
	PRODUCTION: 'PRODUCTION',
	STAGING: 'STAGING',
	TESTING: 'TESTING',
});
